// src/components/PaymentInstructionsBlock.jsx
//
// Sprint 2c-2b-1: Leaf payment instructions display block.
// Migrated verbatim from prototype/between-the-lines-v2.html lines 8007-8130.
//
// Pure props-display component. No state, no helpers, no sub-components.
// Used by Sprint 2c-2b-1: MyPurchasesPanel, PackPurchasePayMePage.
// Theme prop selects color palette (leather default, burgundy + midnight branches).
//
// IMPORTANT — Production caveat (Phase 4 reassessment required):
// QR generation via api.qrserver.com is prototype-compatible display behavior
// only. Before Phase 4 production payment flow deployment, reassess:
//   - Privacy: encoded URLs (WhatsApp number + reference code) exposed to
//     third-party (api.qrserver.com); HK PDPO / GDPR-equivalent considerations
//     for student payment data
//   - Reliability: external service dependency; no fallback path if
//     api.qrserver.com is unavailable, rate-limited, or deprecated
//   - Compliance: third-party data exposure may require user consent or
//     privacy policy disclosure under HK Personal Data (Privacy) Ordinance
//
// Phase 4 options to evaluate (informational):
//   - Self-hosted QR generation (npm qrcode or qrcode-generator, ~5-30 KB,
//     no network dependency)
//   - Cloud Function generation (backend-controlled, Firebase Functions +
//     qrcode npm; aligns with Phase 0 Doc #1 entitlement architecture)
//   - Pre-generated QR codes per pack (admin-managed assets, stable URLs,
//     no runtime third-party calls)
//
// Sprint 2c-2b-1 scope: verbatim extraction only. NO behavior change.

import React from 'react';

const PaymentInstructionsBlock = ({ booking, referenceCode, prefilledMessage, theme = 'leather' }) => {
    const waNumberDigits = (booking?.whatsappNumber || '').replace(/[^\d]/g, '');
    const waLink = waNumberDigits
        ? `https://wa.me/${waNumberDigits}?text=${encodeURIComponent(prefilledMessage || '')}`
        : null;

    const qrApiBase = 'https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=10&data=';
    const waQrUrl = waLink ? qrApiBase + encodeURIComponent(waLink) : null;

    const paymeHandle = (booking?.paymeHandle || '').replace(/^@/, '');
    const paymeQrUrl = booking?.paymeQrImageUrl
        ? booking.paymeQrImageUrl
        : (paymeHandle ? qrApiBase + encodeURIComponent(`https://payme.hsbc/${paymeHandle}`) : null);

    const themeClasses = {
        leather: {
            bg: 'bg-leather-pageDark border-leather-accent/30',
            text: 'text-leather-text',
            muted: 'text-leather-muted',
            accent: 'text-leather-accent',
            link: 'bg-leather-accent text-leather-pageDark hover:bg-leather-accentLight'
        },
        burgundy: {
            bg: 'bg-burgundy-pageDark border-burgundy-accent/30',
            text: 'text-burgundy-text',
            muted: 'text-burgundy-muted',
            accent: 'text-burgundy-accent',
            link: 'bg-burgundy-accent text-burgundy-pageDark hover:bg-burgundy-accentLight'
        },
        midnight: {
            bg: 'bg-midnight-pageDark border-midnight-accent/30',
            text: 'text-midnight-text',
            muted: 'text-midnight-muted',
            accent: 'text-midnight-accent',
            link: 'bg-midnight-accent text-midnight-pageDark hover:bg-midnight-accentLight'
        }
    };
    const t = themeClasses[theme] || themeClasses.leather;

    return (
        <div className={`border ${t.bg} p-5 space-y-4`}>
            <div className="text-center pb-3 border-b border-current/20">
                <div className={`font-display text-xs font-semibold ${t.muted} mb-1`}>
                    Reference Code
                </div>
                <div className={`font-display text-2xl ${t.accent}`}>
                    {referenceCode}
                </div>
                <div className={`font-serif italic text-xs ${t.muted} mt-1`}>
                    Quote this when sending payment
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <div className="text-center">
                    <div className={`font-display text-xs font-semibold ${t.accent} mb-2`}>
                        PayMe
                    </div>
                    {paymeQrUrl && (
                        <img src={paymeQrUrl} alt="PayMe QR"
                             className="mx-auto bg-white p-1 mb-2"
                             style={{ width: 140, height: 140 }} />
                    )}
                    <div className={`font-serif text-sm ${t.text}`}>
                        {booking?.paymeHandle || '—'}
                    </div>
                </div>

                <div className="text-center flex flex-col justify-center">
                    <div className={`font-display text-xs font-semibold ${t.accent} mb-2`}>
                        FPS / Bank Transfer
                    </div>
                    <div className={`font-display text-lg ${t.text}`}>
                        {booking?.fpsNumber || '—'}
                    </div>
                    <div className={`font-serif italic text-xs ${t.muted} mt-1`}>
                        ({booking?.whatsappDisplayName || 'Danielle Lam'})
                    </div>
                </div>
            </div>

            <div className="border-t border-current/20 pt-4">
                <div className={`font-display text-xs font-semibold ${t.accent} mb-3 text-center`}>
                    After Payment — Send Screenshot via WhatsApp
                </div>

                <div className="grid md:grid-cols-2 gap-4 items-center">
                    {waQrUrl && (
                        <div className="text-center">
                            <img src={waQrUrl} alt="WhatsApp QR"
                                 className="mx-auto bg-white p-1"
                                 style={{ width: 140, height: 140 }} />
                            <div className={`font-serif italic text-xs ${t.muted} mt-2`}>
                                Scan with phone
                            </div>
                        </div>
                    )}

                    <div className="text-center">
                        {waLink ? (
                            <a href={waLink} target="_blank" rel="noopener noreferrer"
                               className={`inline-block px-5 py-3 font-display text-sm font-semibold ${t.link} transition-colors btn-sweep`}>
                                Open WhatsApp →
                            </a>
                        ) : (
                            <span className={`font-serif italic ${t.muted}`}>
                                WhatsApp number not configured
                            </span>
                        )}
                        <div className={`font-serif text-sm ${t.text} mt-2`}>
                            {booking?.whatsappNumber || '—'}
                        </div>
                    </div>
                </div>

                {booking?.whatsappBoundaryNote && (
                    <p className={`font-serif italic text-xs ${t.muted} text-center mt-4 px-4`}>
                        {booking.whatsappBoundaryNote}
                    </p>
                )}
            </div>
        </div>
    );
};

export default PaymentInstructionsBlock;
